import { useCallback, useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Archive, RefreshCw, Download, ShieldCheck, Search, Eye, ScrollText, DatabaseZap } from "lucide-react";

function download(filename, text, type) {
  const blob = new Blob([text], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function ArchivePanel() {
  const [archives, setArchives] = useState([]);
  const [preview, setPreview] = useState(null);
  const [detail, setDetail] = useState(null);
  const [verify, setVerify] = useState(null);
  const [audit, setAudit] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [showAudit, setShowAudit] = useState(false);

  const load = useCallback(async () => {
    const [a, p] = await Promise.all([
      api.get(`/admin/archives${q ? `?q=${encodeURIComponent(q)}` : ""}`),
      api.get("/admin/archive/preview"),
    ]);
    setArchives(a.data.items); setPreview(p.data);
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const createArchive = async () => {
    if (!window.confirm("Create a new immutable Development Archive of all current engineering, QA, certification and seed data? Nothing will be deleted.")) return;
    setBusy(true); setMsg("");
    try {
      const { data } = await api.post("/admin/archive", {
        archive_type: "development",
        title: "Development Archive",
        description: "Engineering, QA, certification, testing and launch-preparation history.",
        version: "v1.0",
      });
      setMsg(`Archived ${data.total_documents} documents → ${data.archive_id}`);
      await load();
    } catch (e) { setMsg("Archive failed. Please retry."); }
    setBusy(false);
  };

  const openDetail = async (id) => {
    setVerify(null);
    const { data } = await api.get(`/admin/archives/${id}`);
    setDetail(data);
  };
  const runVerify = async (id) => {
    const { data } = await api.get(`/admin/archives/${id}/verify`);
    setVerify(data);
  };
  const exportArchive = async (id, format) => {
    if (format === "json") {
      const { data } = await api.get(`/admin/archives/${id}`);
      download(`${id}.json`, JSON.stringify(data, null, 2), "application/json");
    } else {
      const { data } = await api.get(`/admin/archives/${id}/export?format=csv`);
      download(`${id}.csv`, typeof data === "string" ? data : JSON.stringify(data), "text/csv");
    }
  };
  const loadAudit = async () => {
    const { data } = await api.get("/admin/audit-logs");
    setAudit(data.items); setShowAudit(true);
  };

  return (
    <div data-testid="archive-panel">
      <div className="toolbar">
        <div className="section-label" style={{ margin: 0 }}>
          <Archive size={14} style={{ verticalAlign: "-2px", marginRight: 6 }} /> Executive Archive — Institutional Memory
        </div>
        <div className="spacer" />
        <div style={{ position: "relative" }}>
          <Search size={13} style={{ position: "absolute", left: 8, top: 8, opacity: 0.5 }} />
          <input data-testid="archive-search" className="input" style={{ paddingLeft: 26, height: 30 }}
                 placeholder="Search archives…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <button className="btn-ghost" onClick={loadAudit} data-testid="view-audit-button"><ScrollText size={13} /> Audit log</button>
        <button className="btn-ghost" onClick={load} disabled={busy}><RefreshCw size={13} /> Refresh</button>
        <button className="btn-primary" onClick={createArchive} disabled={busy} data-testid="create-archive-button">
          <DatabaseZap size={14} /> Create archive
        </button>
      </div>

      {msg && <div className="card" data-testid="archive-msg" style={{ borderColor: "#1F75A8", color: "#1F75A8" }}>{msg}</div>}

      {preview && (
        <div className="card" data-testid="archive-preview" style={{ marginBottom: 16 }}>
          <div className="detail-label">Archive scope (source — never deleted)</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 8 }}>
            {Object.entries(preview.source_counts).map(([c, n]) => (
              <span key={c} style={{ fontSize: 12, background: "#EAF7FF", padding: "4px 10px", borderRadius: 12 }}>
                {c}: <strong>{n}</strong>
              </span>
            ))}
          </div>
          <div style={{ fontSize: 12, marginTop: 10, opacity: 0.7 }}>
            {preview.total_documents} documents · {preview.environment} · MEOS {preview.meos_version} · EII {preview.eii_version}
          </div>
        </div>
      )}

      <div className="section-label">Archived records ({archives.length})</div>
      {archives.length === 0 && <div className="empty-state" data-testid="archive-empty">No archives yet. Create your first immutable Development Archive above.</div>}
      <div style={{ display: "grid", gap: 10 }}>
        {archives.map((a) => (
          <div key={a.archive_id} className="card" data-testid={`archive-row-${a.archive_id}`}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ fontWeight: 600 }}>{a.title} <span style={{ fontSize: 11, opacity: 0.6 }}>· {a.version}</span></div>
                <div style={{ fontSize: 12, opacity: 0.7 }}>{a.archive_id} · {a.total_documents} docs · {new Date(a.created_at).toLocaleString()} · {a.created_by}</div>
                <div style={{ fontSize: 11, opacity: 0.55, marginTop: 2, wordBreak: "break-all" }}>{a.checksum}</div>
              </div>
              <span className="cert-badge cert-yes" style={{ fontSize: 11 }}>IMMUTABLE</span>
              <button className="btn-ghost" onClick={() => openDetail(a.archive_id)} data-testid={`view-archive-${a.archive_id}`}><Eye size={13} /> View</button>
              <button className="btn-ghost" onClick={() => runVerify(a.archive_id)} data-testid={`verify-archive-${a.archive_id}`}><ShieldCheck size={13} /> Verify</button>
              <button className="btn-ghost" onClick={() => exportArchive(a.archive_id, "json")} data-testid={`export-json-${a.archive_id}`}><Download size={13} /> JSON</button>
              <button className="btn-ghost" onClick={() => exportArchive(a.archive_id, "csv")} data-testid={`export-csv-${a.archive_id}`}><Download size={13} /> CSV</button>
            </div>
            {verify && verify.archive_id === a.archive_id && (
              <div data-testid="verify-result" style={{ marginTop: 8, fontSize: 12, color: verify.integrity_ok ? "#1D9E75" : "#C0392B" }}>
                Integrity: <strong>{verify.status}</strong> ({verify.recomputed_checksum.slice(0, 24)}…)
              </div>
            )}
          </div>
        ))}
      </div>

      {detail && (
        <div className="card" data-testid="archive-detail" style={{ marginTop: 16 }}>
          <div className="toolbar" style={{ marginTop: 0 }}>
            <div className="detail-label" style={{ margin: 0 }}>{detail.archive_id} — contents</div>
            <div className="spacer" />
            <button className="btn-ghost" onClick={() => setDetail(null)}>Close</button>
          </div>
          {Object.entries(detail.counts || {}).map(([c, n]) => (
            <div key={c} style={{ fontSize: 12, padding: "4px 0", borderBottom: "1px solid #eee" }}>
              <strong>{c}</strong>: {n} document(s) preserved
            </div>
          ))}
        </div>
      )}

      {showAudit && (
        <div className="card" data-testid="audit-log" style={{ marginTop: 16 }}>
          <div className="toolbar" style={{ marginTop: 0 }}>
            <div className="detail-label" style={{ margin: 0 }}>Audit log ({audit.length})</div>
            <div className="spacer" />
            <button className="btn-ghost" onClick={() => setShowAudit(false)}>Close</button>
          </div>
          {audit.map((l) => (
            <div key={l.audit_id} style={{ fontSize: 12, padding: "6px 0", borderBottom: "1px solid #eee" }}>
              <strong>{l.action}</strong> → {l.target} · {l.actor?.email} · {new Date(l.timestamp).toLocaleString()}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
